import React, { useState, useRef, useEffect } from 'react';

import Layout from '../componentes/Plantilla/Plantilla';
import Breadcrumbs from '../componentes/UI/Breadcrumbs';
import styles from './FAQ.module.css';

const useReveal = (threshold = 0.1) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.unobserve(e.target); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
};

const FAQ_DATA = [
  {
    categoria: "Sobre SEIMA",
    preguntas: [
      {
        q: "¿Cuánta experiencia tienen en el mercado?",
        a: "SEIMA, S.A. cuenta con más de 30 años de experiencia, habiendo sido fundada en 1991. Desde entonces hemos sido un aliado estratégico clave para la industria guatemalteca."
      },
      {
        q: "¿En qué países operan?",
        a: "Además de nuestro liderazgo consolidado en Guatemala, hemos expandido nuestras operaciones y cobertura hacia El Salvador, Honduras y proyectamos seguir creciendo en toda Centroamérica."
      },
      {
        q: "¿Cuál es su modelo de negocio principal?",
        a: "Nos especializamos en el suministro y mantenimiento de equipos industriales, especialmente de lavandería, y servicios de limpieza especializada (como limpieza de tuberías industriales). Atendemos sectores comerciales, hospitalarios y hoteleros con eficiencia de nivel premium."
      }
    ]
  },
  {
    categoria: "Productos y Marcas",
    preguntas: [
      {
        q: "¿Qué marcas de equipos industriales representan?",
        a: "Somos representantes directos de marcas líderes a nivel mundial como Speed Queen, Fagor, Cissell, UniMac, Renzacci, GMP, Image, entre otras, garantizando calidad europea y americana."
      },
      {
        q: "¿Venden repuestos originales?",
        a: "Sí. Para garantizar la vida útil y el rendimiento óptimo de sus equipos, suministramos repuestos 100% originales para todas las marcas que representamos, respaldados con soporte técnico directo."
      }
    ]
  },
  {
    categoria: "Servicios Técnicos",
    preguntas: [
      {
        q: "¿En qué consiste el servicio de limpieza de tuberías?",
        a: "Es un proceso técnico integral que comienza con una boroscopía industrial (inspección por cámara), seguido de un sistema hidrodinámico de alta presión para eliminar incrustaciones sin dañar la tubería, ideal para intercambiadores de calor, evaporadores y calderas."
      },
      {
        q: "¿Ofrecen contratos de mantenimiento preventivo?",
        a: "Sí, diseñamos planes de mantenimiento preventivo adaptados a las necesidades operativas de cada cliente para minimizar el tiempo de inactividad (downtime) y maximizar la eficiencia energética de sus equipos."
      }
    ]
  },
  {
    categoria: "Ubicación y Contacto",
    preguntas: [
      {
        q: "¿Dónde están ubicados físicamente?",
        a: "Nuestras oficinas centrales se encuentran en Blvd. los Próceres 19-08, Empresarial Zona Pradera, Torre 3 Oficina 1009, Zona 10, Ciudad de Guatemala."
      },
      {
        q: "¿Cómo puedo solicitar una cotización o asesoría?",
        a: "Puede llamarnos al +502 3097 8165, escribirnos a info@seima.com.gt, o utilizar el formulario en nuestra página de Contacto. Nuestro equipo de ingenieros le brindará atención personalizada."
      }
    ]
  }
];

const Pregunta = ({ faq, isOpen, onClick }) => {
  const contentRef = useRef(null);
  
  return (
    <div className={`${styles.preguntaItem} ${isOpen ? styles.abierto : ''}`}>
      <button className={styles.preguntaHeader} onClick={onClick}>
        <span className={styles.preguntaTexto}>{faq.q}</span>
        <span className={styles.preguntaIcono}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 9l-7 7-7-7" />
          </svg>
        </span>
      </button>
      <div 
        className={styles.respuestaWrapper}
        style={{ height: isOpen ? `${contentRef.current?.scrollHeight}px` : '0px' }}
      >
        <div className={styles.respuestaContenido} ref={contentRef}>
          <p>{faq.a}</p>
        </div>
      </div>
    </div>
  );
};

const FAQPage = () => {
  const [heroRef, heroVisible] = useReveal(0.1);
  const [contentRef, contentVisible] = useReveal(0.05);
  
  const [categoriaActiva, setCategoriaActiva] = useState(FAQ_DATA[0].categoria);
  const [preguntaAbierta, setPreguntaAbierta] = useState(null);

  const categorias = FAQ_DATA.map(d => d.categoria);
  const preguntasMostradas = FAQ_DATA.find(d => d.categoria === categoriaActiva)?.preguntas || [];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Layout>
      <main className={styles.faqPage}>
        {/* HERO SECTION */}
        <section className={styles.hero} ref={heroRef}>
          <div className={`container ${styles.heroContainer} ${heroVisible ? styles.visible : ''}`}>
            <Breadcrumbs 
              paths={[
                { label: 'Inicio', to: '/' },
                { label: 'Preguntas Frecuentes' }
              ]}
            />
            <h1 className={styles.heroTitulo}>
              Preguntas <span className={styles.heroAcento}>Frecuentes</span>
            </h1>
            <div className={styles.lineaTitulo} />
            <p className={styles.heroSub}>
              Encuentra respuestas rápidas sobre nuestra historia, productos, servicios especializados y cómo podemos impulsar tu industria.
            </p>
          </div>
        </section>

        {/* CONTENIDO PRINCIPAL */}
        <section className={styles.contenido} ref={contentRef}>
          <div className={`container ${styles.gridContainer} ${contentVisible ? styles.visible : ''}`}>
            
            {/* Sidebar Categorías */}
            <aside className={styles.sidebar}>
              <div>
                <h3 className={styles.sidebarTitulo}>Categorías</h3>
                <div className={styles.lineaCat} />
              </div>
              <div className={styles.categoriasLista}>
                {categorias.map(cat => (
                  <button
                    key={cat}
                    className={`${styles.catBtn} ${categoriaActiva === cat ? styles.catActiva : ''}`}
                    onClick={() => {
                      setCategoriaActiva(cat);
                      setPreguntaAbierta(null);
                    }}
                  >
                    {cat}
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </button>
                ))}
              </div>
              
              <div className={styles.contactoCard}>
                <h4>¿Aún tienes dudas?</h4>
                <p>Nuestro equipo de ingenieros está listo para brindarte asesoría técnica.</p>
                <a href="/#contact" className={styles.contactoBtn}>Contáctanos</a>
              </div>
            </aside>

            {/* Lista de Preguntas */}
            <div className={styles.preguntasContainer}>
              <div>
                <h2 className={styles.catTituloActual}>{categoriaActiva}</h2>
                <div className={styles.lineaCat} />
              </div>
              <div className={styles.acordeon}>
                {preguntasMostradas.map((faq, i) => (
                  <Pregunta 
                    key={i} 
                    faq={faq} 
                    isOpen={preguntaAbierta === i} 
                    onClick={() => setPreguntaAbierta(preguntaAbierta === i ? null : i)} 
                  />
                ))}
              </div>
            </div>

          </div>
        </section>
      </main>
    </Layout>
  );
};

export default FAQPage;
